import React, { useState, lazy } from 'react';
import { MapPin, Phone, Clock, Instagram, Facebook, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
export function Footer() {
  const {
    t,
    language
  } = useLanguage();
  const [modal, setModal] = useState<'privacy' | 'terms' | null>(null);
  const privacyContent = language === 'es' ? {
    title: 'Política de Privacidad',
    sections: [{
      heading: 'Información que Recopilamos',
      text: 'Recopilamos información personal que usted nos proporciona voluntariamente al agendar una cita, realizar un pago, o contactarnos a través de WhatsApp, teléfono o formularios. Esto puede incluir su nombre, número de teléfono, dirección de correo electrónico y tipo de piel o condiciones relevantes para su tratamiento.'
    }, {
      heading: 'Uso de la Información',
      text: 'Utilizamos su información personal exclusivamente para: programar y gestionar sus citas, personalizar su tratamiento de acuerdo a sus necesidades, procesar pagos de forma segura a través de Stripe, y comunicarnos con usted sobre sus servicios.'
    }, {
      heading: 'Protección de Datos',
      text: 'Su información personal está protegida y nunca será vendida, compartida o distribuida a terceros sin su consentimiento expreso. Los pagos se procesan de forma segura a través de Stripe, y no almacenamos datos de tarjetas de crédito en nuestros servidores.'
    }, {
      heading: 'Cookies y Análisis',
      text: 'Nuestro sitio web puede utilizar cookies para mejorar su experiencia de navegación. Estas cookies no recopilan información personal identificable.'
    }, {
      heading: 'Sus Derechos',
      text: 'Usted tiene derecho a solicitar acceso, corrección o eliminación de su información personal en cualquier momento contactándonos directamente.'
    }, {
      heading: 'Contacto',
      text: 'Para preguntas sobre esta política, contáctenos al (347) 264-8498 o visite nuestro local en 32-60 84th St, East Elmhurst, NY 11370.'
    }]
  } : {
    title: 'Privacy Policy',
    sections: [{
      heading: 'Information We Collect',
      text: 'We collect personal information that you voluntarily provide when booking an appointment, making a payment, or contacting us via WhatsApp, phone, or forms. This may include your name, phone number, email address, and skin type or conditions relevant to your treatment.'
    }, {
      heading: 'Use of Information',
      text: 'We use your personal information exclusively to: schedule and manage your appointments, personalize your treatment according to your needs, process payments securely through Stripe, and communicate with you about your services.'
    }, {
      heading: 'Data Protection',
      text: 'Your personal information is protected and will never be sold, shared, or distributed to third parties without your express consent. Payments are processed securely through Stripe, and we do not store credit card data on our servers.'
    }, {
      heading: 'Cookies and Analytics',
      text: 'Our website may use cookies to improve your browsing experience. These cookies do not collect personally identifiable information.'
    }, {
      heading: 'Your Rights',
      text: 'You have the right to request access, correction, or deletion of your personal information at any time by contacting us directly.'
    }, {
      heading: 'Contact',
      text: 'For questions about this policy, contact us at (347) 264-8498 or visit us at 32-60 84th St, East Elmhurst, NY 11370.'
    }]
  };
  const termsContent = language === 'es' ? {
    title: 'Términos y Condiciones',
    sections: [{
      heading: 'Servicios',
      text: 'Lorena Beauty House ofrece servicios de estética facial, corporal, láser y formación profesional. Todos los tratamientos son personalizados y requieren una evaluación previa.'
    }, {
      heading: 'Citas y Cancelaciones',
      text: 'Las citas deben agendarse con anticipación a través de WhatsApp o teléfono. Solicitamos un aviso de al menos 24 horas para cancelaciones o reprogramaciones. Las cancelaciones tardías o ausencias repetidas pueden resultar en la pérdida del depósito.'
    }, {
      heading: 'Pagos',
      text: 'Aceptamos pagos a través de Stripe (tarjeta de crédito/débito) y efectivo. Los pagos por servicios son finales una vez realizado el tratamiento. Los depósitos para Master Class no son reembolsables.'
    }, {
      heading: 'Resultados',
      text: 'Los resultados de los tratamientos varían según el tipo de piel, condición y adherencia al plan de cuidado recomendado. Lorena Beauty House no garantiza resultados específicos, pero se compromete a utilizar las mejores técnicas y productos disponibles.'
    }, {
      heading: 'Responsabilidad',
      text: 'El cliente es responsable de informar sobre alergias, condiciones médicas o medicamentos que puedan afectar el tratamiento. Lorena Beauty House no se hace responsable por reacciones adversas no reportadas previamente.'
    }, {
      heading: 'Propiedad Intelectual',
      text: 'Todo el contenido de este sitio web, incluyendo textos, imágenes, logotipos y diseño, es propiedad de Lorena Beauty House y está protegido por las leyes de derechos de autor.'
    }]
  } : {
    title: 'Terms & Conditions',
    sections: [{
      heading: 'Services',
      text: 'Lorena Beauty House offers facial, body, laser aesthetics, and professional training services. All treatments are personalized and require a prior evaluation.'
    }, {
      heading: 'Appointments & Cancellations',
      text: 'Appointments must be scheduled in advance via WhatsApp or phone. We request at least 24 hours notice for cancellations or rescheduling. Late cancellations or repeated no-shows may result in deposit forfeiture.'
    }, {
      heading: 'Payments',
      text: 'We accept payments through Stripe (credit/debit card) and cash. Payments for services are final once the treatment is performed. Master Class deposits are non-refundable.'
    }, {
      heading: 'Results',
      text: 'Treatment results vary depending on skin type, condition, and adherence to the recommended care plan. Lorena Beauty House does not guarantee specific results but is committed to using the best available techniques and products.'
    }, {
      heading: 'Liability',
      text: 'The client is responsible for disclosing allergies, medical conditions, or medications that may affect treatment. Lorena Beauty House is not liable for adverse reactions not previously reported.'
    }, {
      heading: 'Intellectual Property',
      text: 'All content on this website, including text, images, logos, and design, is the property of Lorena Beauty House and is protected by copyright laws.'
    }]
  };
  const activeContent = modal === 'privacy' ? privacyContent : termsContent;
  return <footer className="bg-gradient-to-b from-charcoal to-espresso text-white pt-16 pb-8 border-t-2 border-gold/40" data-id="element-114">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-115">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12" data-id="element-116">
          <div className="flex flex-col items-center md:items-start" data-id="element-117">
            <div className="mb-6 text-center md:text-left" data-id="element-118">
              <p className="font-serif text-3xl text-gold font-bold italic leading-tight" data-id="element-119">
                Lorena
              </p>
              <p className="font-serif text-lg text-gold/80 tracking-widest" data-id="element-120">
                Beauty House
              </p>
              <div className="w-12 h-px bg-gold/40 mt-3 mx-auto md:mx-0" data-id="element-121"></div>
            </div>
            <p className="text-white/70 text-sm max-w-xs text-center md:text-left" data-id="element-122">
              {t('footer', 'desc')}
            </p>
            <div className="flex items-center space-x-4 mt-5" data-id="element-123">
              <a href="https://www.instagram.com/lorenabeautyhouse" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-white transition-all duration-300" aria-label="Instagram" data-id="element-124">
                <Instagram className="w-4 h-4" data-id="element-125" />
              </a>
              <a href="https://www.facebook.com/lorenabeautyhouse" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-white transition-all duration-300" aria-label="Facebook" data-id="element-126">
                <Facebook className="w-4 h-4" data-id="element-127" />
              </a>
            </div>
          </div>

          <div data-id="element-128">
            <h3 className="font-serif text-xl text-gold mb-6" data-id="element-129">
              {t('footer', 'contact')}
            </h3>
            <ul className="space-y-4" data-id="element-130">
              <li className="flex items-start space-x-3" data-id="element-131">
                <Phone className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" data-id="element-132" />
                <div className="text-white/80 text-sm" data-id="element-133">
                  <p data-id="element-134">(347) 264-8498</p>
                  <p data-id="element-135">(917) 396-4125</p>
                </div>
              </li>
              <li className="flex items-start space-x-3" data-id="element-136">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" data-id="element-137" />
                <p className="text-white/80 text-sm" data-id="element-138">
                  32-60 84th St,
                  <br data-id="element-139" />
                  East Elmhurst, NY 11370
                  <br data-id="element-140" />
                  Queens, New York
                </p>
              </li>
            </ul>
          </div>

          <div data-id="element-141">
            <h3 className="font-serif text-xl text-gold mb-6" data-id="element-142">
              {t('footer', 'hours')}
            </h3>
            <ul className="space-y-4" data-id="element-143">
              <li className="flex items-start space-x-3" data-id="element-144">
                <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" data-id="element-145" />
                <p className="text-white/80 text-sm" data-id="element-146">
                  {t('footer', 'hoursDesc1')}
                  <br data-id="element-147" />
                  {t('footer', 'hoursDesc2')}
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* Google Map */}
        <div className="mb-12 rounded-md overflow-hidden border-2 border-gold/20 shadow-lg" data-id="element-148">
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3021.5!2d-73.8654!3d40.7627!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25f0418a3e1e7%3A0x0!2s32-60+84th+St%2C+East+Elmhurst%2C+NY+11370!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus" width="100%" height="250" style={{
          border: 0
        }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Lorena Beauty House — Queens, New York" className="w-full" data-id="element-149"></iframe>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center" data-id="element-150">
          <p className="text-white/50 text-xs text-center md:text-left mb-4 md:mb-0" data-id="element-151">
            &copy; {new Date().getFullYear()} Lorena Beauty House.{' '}
            {t('footer', 'rights')}
          </p>
          <div className="flex items-center space-x-4" data-id="element-152">
            <button onClick={() => setModal('privacy')} className="text-white/50 text-xs hover:text-gold transition-colors" data-id="element-153">
              {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
            </button>
            <span className="text-white/30" data-id="element-154">|</span>
            <button onClick={() => setModal('terms')} className="text-white/50 text-xs hover:text-gold transition-colors" data-id="element-155">
              {language === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions'}
            </button>
          </div>
        </div>
      </div>

      {/* Privacy / Terms Modal */}
      <AnimatePresence data-id="element-156">
        {modal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModal(null)} data-id="element-157">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-md shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-158">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20 bg-beige-light" data-id="element-159">
                <h3 className="text-xl font-serif font-bold text-dark" data-id="element-160">
                  {activeContent.title}
                </h3>
                <button onClick={() => setModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gold/10 transition-colors" data-id="element-161">
                  <X className="w-5 h-5 text-dark/60" data-id="element-162" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-130px)] space-y-6" data-id="element-163">
                <p className="text-dark/50 text-sm italic" data-id="element-164">
                  {language === 'es' ? `Última actualización: ${new Date().toLocaleDateString('es-ES', {
                month: 'long',
                year: 'numeric'
              })}` : `Last updated: ${new Date().toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}`}
                </p>
                {activeContent.sections.map((section, i) => <div key={i} data-id="element-165">
                    <h4 className="font-bold text-dark mb-2 text-sm uppercase tracking-wide" data-id="element-166">
                      {section.heading}
                    </h4>
                    <p className="text-dark/70 text-sm leading-relaxed" data-id="element-167">
                      {section.text}
                    </p>
                  </div>)}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gold/10 bg-beige-light/50" data-id="element-168">
                <button onClick={() => setModal(null)} className="w-full bg-gold hover:bg-gold-dark text-white py-2.5 rounded-sm font-medium text-sm transition-colors" data-id="element-169">
                  {language === 'es' ? 'Entendido' : 'Got it'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </footer>;
}